import React from "react";
import { Link } from 'react-router-dom';
import Navbar from './Navbar';
import { useAuth } from './AuthContext';
import SEO from './components/SEO';

function GuiPreview() {
  return (
    <div className="bg-gradient-to-br from-indigo-100 to-cyan-100 rounded-2xl shadow-xl p-8 w-full max-w-xs md:max-w-sm lg:max-w-md">
      <div className="bg-white rounded-lg shadow p-6">
        <div className="mb-6">
          <div className="font-semibold text-indigo-700">Sample PowerShell GUI</div>
          <ul className="text-xs text-gray-700 mt-1">
            <li>✔ Button: Run Script</li>
            <li>✔ TextBox: Input Path</li>
            <li>✔ DataGrid: Results Table</li>
          </ul>
        </div>
        <div className="mb-6">
          <div className="font-semibold text-pink-700">Drag & Drop Controls</div>
          <ul className="text-xs text-gray-700 mt-1">
            <li>✔ Visual Layout Editor</li>
            <li>✔ Snap to Grid</li>
          </ul>
        </div>
        <div>
          <div className="font-semibold text-green-700">Export to PowerShell</div>
          <ul className="text-xs text-gray-700 mt-1">
            <li>✔ Instant Script Generation</li>
            <li>✔ Ready to Use</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default function LandingPage() {
  const { user } = useAuth();
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <SEO 
        title="PowerShell GUI Builder - Create Windows Applications with Drag & Drop"
        description="Build beautiful PowerShell GUI applications with drag-and-drop interface. Create, design, and export professional Windows applications easily. No coding required."
        canonical="/"
      />
      <Navbar />
      <main className="flex-1 flex flex-col justify-center items-center px-4 py-12">
        <div className="w-full max-w-6xl flex flex-col lg:flex-row items-center justify-between gap-12 lg:gap-20">
          {/* Left: Hero */}
          <div className="flex-1 flex flex-col items-center lg:items-start text-center lg:text-left">
            <span className="inline-block bg-gray-200 text-xs px-2 py-1 rounded mb-3">Introducing PowerShell GUI Builder</span>
            <h1 className="text-4xl md:text-5xl font-bold mb-5">
              Build Beautiful <span className="text-cyan-500">PowerShell GUIs</span>
              <br /> With Drag & Drop Ease
            </h1>
            <p className="text-lg text-gray-600 mb-8 max-w-xl">
              Instantly design, customize, and export user interfaces for your PowerShell scripts. No XAML or WinForms knowledge required.
            </p>
            <Link to="/register">
              <button className="bg-indigo-600 text-white px-8 py-3 rounded font-semibold shadow hover:bg-indigo-700 transition text-lg">
                Start Building Free
              </button>
            </Link>
          </div>
          {/* Right: Preview */}
          <div className="flex-1 flex justify-center items-center w-full">
            <GuiPreview />
          </div>
        </div>
      </main>
      {/* How It Works */}
      <section className="w-full bg-white py-16 mt-8 shadow-inner">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-center mb-12">How It Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gray-50 rounded-xl shadow p-8 text-center flex flex-col items-center">
              <div className="text-3xl font-bold mb-3">1</div>
              <h3 className="font-semibold mb-3">Design Your GUI</h3>
              <p className="text-gray-500 text-base">Drag and drop buttons, textboxes, grids, and more onto your canvas. Arrange and customize with instant feedback.</p>
            </div>
            <div className="bg-gray-50 rounded-xl shadow p-8 text-center flex flex-col items-center">
              <div className="text-3xl font-bold mb-3">2</div>
              <h3 className="font-semibold mb-3">Configure Properties</h3>
              <p className="text-gray-500 text-base">Set control properties, events, and layout options visually—no manual XAML editing required.</p>
            </div>
            <div className="bg-gray-50 rounded-xl shadow p-8 text-center flex flex-col items-center">
              <div className="text-3xl font-bold mb-3">3</div>
              <h3 className="font-semibold mb-3">Export PowerShell</h3>
              <p className="text-gray-500 text-base">Download a ready-to-run PowerShell script for your GUI. Use it in your automation, tools, or admin tasks.</p>
            </div>
          </div>
        </div>
      </section>
      {/* Pricing Section (only show if not logged in) */}
      {!user && (
        <section className="w-full bg-indigo-50 py-16 border-t border-indigo-100">
          <div className="max-w-3xl mx-auto px-4">
            <h2 className="text-2xl font-bold text-center mb-10 text-indigo-800">Pricing</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl shadow p-8 flex flex-col items-center">
                <div className="text-3xl font-bold text-indigo-700 mb-2">Free</div>
                <div className="text-2xl font-semibold mb-4">£0<span className="text-base font-normal">/mo</span></div>
                <ul className="text-gray-700 text-sm mb-6 space-y-1">
                  <li>✔ Unlimited GUI design</li>
                  <li>✔ Drag & drop editor</li>
                  <li>✔ All controls & layout tools</li>
                  <li>✖ Export PowerShell scripts</li>
                  <li>✖ Save/load projects</li>
                </ul>
                <Link to="/register" className="w-full">
                  <button className="w-full bg-indigo-600 text-white py-2 rounded font-semibold hover:bg-indigo-700 transition">Get Started Free</button>
                </Link>
              </div>
              <div className="bg-white rounded-xl shadow p-8 flex flex-col items-center border-2 border-indigo-600">
                <div className="text-3xl font-bold text-indigo-700 mb-2">Pro</div>
                <div className="text-2xl font-semibold mb-4">£5<span className="text-base font-normal">/mo</span></div>
                <ul className="text-gray-700 text-sm mb-6 space-y-1">
                  <li>✔ Unlimited GUI design</li>
                  <li>✔ Drag & drop editor</li>
                  <li>✔ All controls & layout tools</li>
                  <li>✔ Export PowerShell scripts</li>
                  <li>✔ Save/load projects</li>
                </ul>
                <Link to="/register" className="w-full">
                  <button className="w-full bg-indigo-700 text-white py-2 rounded font-semibold hover:bg-indigo-800 transition">Upgrade to Pro</button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
} 